/* build: 2026-06-16a (calm the FB/IG webview: drop the over-eager 1.6s "Trenger du hjelp? / Åpne i nettleseren" help strip that covered a working calendar) */
/*
 * nono-booking.js — shared booking-modal controller (elite-hardened 2026-06-09).
 *
 * Binds every [data-booking-trigger] to the single #lp-booking-modal and runs
 * a robust open -> book -> confirm state machine designed for the reality that
 * ~90% of paid traffic is the FB/IG in-app webview and that the booking happens
 * inside a CROSS-ORIGIN Momence iframe we cannot read.
 *
 * Confirmation strategy (the iframe handshake is NOT trustworthy — Shopify forces
 * X-Frame-Options: DENY on /pages/booket, so the same-origin postMessage can
 * never fire in-frame in production):
 *   1. SERVER POLL (primary): after open, poll the backend /api/booking-status
 *      keyed by fbclid/_fbp. The Momence members-sync fires Schedule within ~2
 *      min of a real booking; when it has, the overlay confirms — iframe-
 *      independent, survives the webview.
 *   2. SAME-ORIGIN postMessage (nono:booking-complete) — kept for the case where
 *      Momence redirects the TOP window (renders fine) or booket ever becomes
 *      framable. Harmless if it never arrives.
 *   3. DWELL prompt: if no confirmation after a while, a non-blocking
 *      "check your email / call us" nudge so a real booker is never left silent.
 * Cross-origin Momence messages are NEVER treated as success (no substring
 * guessing — that caused false "Booket!" mid-flow). Every overlay is dismissible
 * and fully reset on open/close so one glitch can never brick the modal.
 */
(function () {
  'use strict';

  // ---- In-app webview detection ------------------------------------------
  function isInAppBrowser() {
    var ua = navigator.userAgent || navigator.vendor || '';
    return /FBAN|FBAV|FB_IAB|FBIOS|Instagram|Messenger|Line\/|LinkedInApp|Snapchat|TikTok|musical_ly|Twitter|Pinterest/i.test(ua);
  }
  function isAndroid() { return /Android/i.test(navigator.userAgent || ''); }
  function isIOS() { return /iPhone|iPad|iPod/i.test(navigator.userAgent || ''); }

  // ---- Early capture-phase opener ----------------------------------------
  var opener = null;
  var pendingOpen = false;
  var HEADER_CTA_SELECTOR = 'a[href="#memberCreateForm"], a[href="#lp-booking"]';
  function triggerFor(target) {
    if (!target || !target.closest) return null;
    return target.closest('[data-booking-trigger]') || target.closest(HEADER_CTA_SELECTOR);
  }
  document.addEventListener('click', function (e) {
    var trig = triggerFor(e.target);
    if (!trig) return;
    // Own-booking pages ([data-nono-booking]) own these triggers — the legacy
    // Momence modal stands down so a stale cached page that still ships this
    // script can never double-open on top of the in-house booking widget.
    if (document.querySelector('[data-nono-booking]')) return;
    if (!opener && !document.getElementById('lp-booking-modal')) return;
    e.preventDefault();
    if (opener) opener();
    else pendingOpen = true;
  }, true);

  function init() {
    // Own-booking pages ship their own widget ([data-nono-booking]) — never
    // bind/build/prewarm the legacy Momence modal there.
    if (document.querySelector('[data-nono-booking]')) return;
    var modal = document.getElementById('lp-booking-modal');
    if (!modal || modal.dataset.bookingBound === '1') return;
    modal.dataset.bookingBound = '1';

    var host = modal.querySelector('[data-booking-iframe-host]');
    var skeleton = modal.querySelector('[data-booking-skeleton]');
    var success = modal.querySelector('[data-booking-success]');
    var panel = modal.querySelector('.lp-booking-modal__panel');
    var body = modal.querySelector('.lp-booking-modal__body');
    var foot = modal.querySelector('.lp-booking-modal__foot');
    var iframeUrl = modal.getAttribute('data-booking-iframe-url');
    var prewarm = modal.getAttribute('data-booking-prewarm') === '1';
    var attributionEndpoint = (modal.getAttribute('data-attribution-endpoint') || '').trim();
    var supportPhone = (modal.getAttribute('data-support-phone') || '+4740600235').trim();
    var debug = modal.getAttribute('data-booking-debug') === '1'; // opt-in Momence message logging
    var inApp = isInAppBrowser();

    var iframeBuilt = false;
    var iframeLoads = 0;
    var bookedBeaconSent = false; // one 'lp_booked' beacon per page view, never reset on modal reopen
    // 2nd-load arbiter state (2026-06-11): a 2nd full-document load is only
    // "probably booked" if the frame goes MUTE afterwards (the XFO'd booket
    // page can't postMessage; a live calendar keeps talking). Instant success
    // on 2nd load told at least one real visitor "Booket!" mid-flow today.
    var load2At = 0;
    var momenceMsgAt = 0;
    var modalOpenedAt = 0; // last modal open — the booking-status poll only confirms bookings after this
    var arbiterTimer = null;
    var pendingEl = null;
    var pendingShown = false;
    var pendingMorphTimer = null;
    var modalEverOpened = false;
    var initiateFired = false;
    var lastFocused = null;
    var scrollY = 0;

    // Overlay state (all resettable)
    var hintShown = false;
    var escapeShown = false;
    var successShown = false;
    var momenceMessageSeen = false;
    var momenceMsgCount = 0;

    // Timers
    var stallTimer = null;   // pre-load: never loaded
    var hintTimer = null;    // post-load: show the non-blocking help bar
    var dwellTimer = null;   // post-load: "did you book?" nudge
    var pollTimer = null;    // server booking-status poll
    var pollStop = 0;

    var infoEl = null;       // single bottom strip (help -> dwell)
    var escapeEl = null;
    var cookieBanner = null;

    // ---- helpers ---------------------------------------------------------
    function parseCookies() {
      var c = {};
      (document.cookie || '').split(';').forEach(function (p) {
        var i = p.indexOf('=');
        if (i <= 0) return;
        var k = p.slice(0, i).trim();
        var raw = p.slice(i + 1);
        try { c[k] = decodeURIComponent(raw); } catch (e) { c[k] = raw; }
      });
      return c;
    }
    function phoneDisplay() {
      var d = supportPhone.replace(/[^0-9]/g, '');
      if (d.indexOf('47') === 0) d = d.slice(2);
      if (d.length === 8) return d.slice(0, 3) + ' ' + d.slice(3, 5) + ' ' + d.slice(5);
      return supportPhone;
    }
    function externalBrowserHref(targetUrl) {
      var href = targetUrl || window.location.href;
      try {
        var u = new URL(href);
        if (isAndroid()) {
          // No package pin (Samsung Internet / Firefox users) + a fallback URL so
          // the tap always resolves to *some* browser or the plain https page.
          return 'intent://' + u.host + u.pathname + u.search +
            '#Intent;scheme=https;S.browser_fallback_url=' + encodeURIComponent(href) + ';end';
        }
        if (isIOS()) {
          // Apple-supported scheme that breaks a webview out to Safari without
          // requiring Chrome (the previous googlechromes:// no-op'd for most users).
          return 'x-safari-' + (u.protocol === 'https:' ? 'https://' : 'http://') + u.host + u.pathname + u.search;
        }
      } catch (e) {}
      return href;
    }
    function styleBtn(a, kind) {
      if (kind === 'primary') a.style.cssText = 'display:inline-flex;align-items:center;justify-content:center;min-height:52px;padding:0.9rem 1.6rem;border-radius:999px;background:linear-gradient(145deg,#580B26 6.58%,#B71F60 94.27%);color:#fff;font-weight:700;text-decoration:none;box-shadow:0 12px 28px rgba(88,11,38,0.25);';
      else if (kind === 'phone') a.style.cssText = 'display:inline-flex;align-items:center;justify-content:center;min-height:52px;padding:0.9rem 1.6rem;border-radius:999px;background:#fff;color:#580B26;border:2px solid #580B26;font-weight:700;text-decoration:none;';
      else if (kind === 'small') a.style.cssText = 'display:inline-flex;align-items:center;min-height:40px;padding:0.4rem 0.9rem;border-radius:999px;background:linear-gradient(145deg,#580B26 6.58%,#B71F60 94.27%);color:#fff;font-weight:700;font-size:0.9rem;text-decoration:none;';
      else a.style.cssText = 'color:#580B26;font-weight:700;text-decoration:underline;text-underline-offset:3px;font-size:0.9rem;min-height:40px;display:inline-flex;align-items:center;';
      return a;
    }
    function openLinkEl(kind) {
      var a = document.createElement('a');
      a.textContent = 'Åpne i nettleseren';
      a.setAttribute('href', externalBrowserHref());
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener');
      return styleBtn(a, kind);
    }
    function telEl(kind, label) {
      var a = document.createElement('a');
      a.setAttribute('href', 'tel:' + supportPhone);
      a.textContent = (label || 'Ring oss: ') + phoneDisplay();
      return styleBtn(a, kind);
    }
    function copyLinkBtn() {
      var b = document.createElement('button');
      b.type = 'button';
      b.textContent = 'Kopier lenke';
      styleBtn(b, 'tel');
      b.addEventListener('click', function () {
        var done = function () { b.textContent = 'Lenke kopiert ✓'; };
        try {
          if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(window.location.href).then(done, function () {});
          else done();
        } catch (e) {}
      });
      return b;
    }

    // ---- single bottom info strip (help / dwell) -------------------------
    function ensureInfo() {
      if (infoEl || !body) return infoEl;
      infoEl = document.createElement('div');
      infoEl.setAttribute('data-booking-info', '');
      infoEl.style.cssText = 'position:absolute;left:0;right:0;bottom:0;z-index:3;display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:0.35rem 0.9rem;padding:0.6rem 1rem calc(0.6rem + env(safe-area-inset-bottom,0px));background:rgba(254,246,236,0.97);border-top:1px solid rgba(88,11,38,0.18);box-shadow:0 -6px 16px rgba(40,8,18,0.08);';
      body.appendChild(infoEl);
      return infoEl;
    }
    function setInfo(nodes) {
      ensureInfo();
      if (!infoEl) return;
      infoEl.innerHTML = '';
      nodes.forEach(function (n) { if (n) infoEl.appendChild(n); });
    }
    function clearInfo() {
      if (infoEl) { try { infoEl.remove(); } catch (e) {} infoEl = null; }
    }
    function showHint() {
      if (hintShown || escapeShown || successShown) return;
      hintShown = true;
      var t = document.createElement('span');
      t.textContent = 'Trenger du hjelp?';
      t.style.cssText = 'font-size:0.9rem;font-weight:600;color:#580B26;';
      setInfo([t, openLinkEl('small'), telEl('label', 'Ring: ')]);
    }
    function showDwell() {
      if (successShown || escapeShown) return;
      var t = document.createElement('span');
      t.textContent = 'Fikk du booket? Sjekk e-posten din for bekreftelse.';
      t.style.cssText = 'font-size:0.9rem;font-weight:600;color:#580B26;';
      setInfo([t, telEl('label', 'Ring: ')]);
      hintShown = true;
    }

    // ---- full escape overlay (only on real stall/error) ------------------
    function buildEscape() {
      if (escapeEl || !body) return;
      escapeEl = document.createElement('div');
      escapeEl.setAttribute('data-booking-escape', '');
      escapeEl.setAttribute('role', 'alertdialog');
      escapeEl.setAttribute('aria-label', 'Åpne bookingen i nettleseren');
      escapeEl.style.cssText = 'position:absolute;inset:0;z-index:4;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0.9rem;padding:1.5rem;text-align:center;background:linear-gradient(160deg,#fef6ec 0%,#fdeee6 100%);';

      var h = document.createElement('h3');
      h.textContent = 'Nesten der – vi åpner bookingen i et større vindu';
      h.style.cssText = 'margin:0;font-size:1.25rem;font-weight:800;color:#1a1a1a;max-width:380px;';

      var p = document.createElement('p');
      p.style.cssText = 'margin:0;max-width:360px;color:#444;font-size:1rem;line-height:1.5;';
      p.textContent = 'Av og til må booking åpnes utenfor Facebook/Instagram. Det tar 5 sekunder – trykk på den rosa knappen under. Prøvetimen er fortsatt 100 % gratis, helt uten binding.';

      escapeEl.appendChild(h);
      escapeEl.appendChild(p);
      escapeEl.appendChild(openLinkEl('primary'));
      escapeEl.appendChild(telEl('phone', 'Eller ring oss: '));
      if (isIOS()) {
        var ios = document.createElement('p');
        ios.style.cssText = 'margin:2px 0 0;color:#777;font-size:0.82rem;max-width:320px;';
        ios.textContent = 'På iPhone kan du også trykke «•••» øverst og velge «Åpne i Safari».';
        escapeEl.appendChild(ios);
      }
      escapeEl.appendChild(copyLinkBtn());
      body.appendChild(escapeEl);
    }
    function showEscape() {
      if (escapeShown || successShown) return; // success always wins
      escapeShown = true;
      clearInfo(); hintShown = false; // full escape supersedes the help strip
      buildEscape();
      if (skeleton) skeleton.setAttribute('hidden', '');
      var first = escapeEl && escapeEl.querySelector('a');
      if (first) { try { first.focus({ preventScroll: true }); } catch (e) { try { first.focus(); } catch (e2) {} } }
    }
    function removeEscape() {
      if (escapeEl) { try { escapeEl.remove(); } catch (e) {} escapeEl = null; }
      escapeShown = false;
    }

    // ---- always-available, calm in-app escape (footer link) --------------
    // Replaces the old 1.6s auto-popping help strip. A small, PERMANENT link in
    // the footer — never covers the calendar, never auto-pops — so a genuinely
    // STUCK in-app visitor can break out to a real browser ANY time (not gated
    // behind a timer that might never fire). It opens the Momence booking page
    // DIRECTLY (first-party in Safari/Chrome — no landing-page loop), dropping a
    // stuck booker straight onto a working calendar. In-app webviews only.
    function addFooterEscape() {
      if (!inApp || !foot || foot.querySelector('[data-booking-foot-escape]')) return;
      try { foot.style.flexWrap = 'wrap'; } catch (e) {}
      var a = document.createElement('a');
      a.setAttribute('data-booking-foot-escape', '');
      a.setAttribute('href', externalBrowserHref(iframeUrl));
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener');
      a.textContent = 'Sitter du fast? Åpne i nettleseren';
      a.style.cssText = 'flex:0 0 100%;width:100%;margin-top:2px;text-align:center;color:#8a5468;font-size:0.78rem;font-weight:600;text-decoration:underline;text-underline-offset:2px;';
      foot.appendChild(a);
    }

    // ---- pending ("we're confirming") overlay -----------------------------
    // Shown when the 2nd-load arbiter says "probably booked". Honest wording:
    // we do NOT claim the booking is confirmed — the server poll upgrades to
    // the real success overlay, and a late Momence message tears this down
    // (the calendar turned out to be alive => let the visitor continue).
    function showPending() {
      if (pendingShown || successShown || escapeShown || !body) return;
      pendingShown = true;
      clearInfo(); hintShown = false;
      if (skeleton) skeleton.setAttribute('hidden', '');
      pendingEl = document.createElement('div');
      pendingEl.setAttribute('data-booking-pending', '');
      pendingEl.setAttribute('role', 'status');
      pendingEl.style.cssText = 'position:absolute;inset:0;z-index:4;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0.9rem;padding:1.5rem;text-align:center;background:linear-gradient(160deg,#fef6ec 0%,#fdeee6 100%);';
      var h = document.createElement('h3');
      h.textContent = 'Takk! Ser ut som bookingen gikk gjennom 🎉';
      h.style.cssText = 'margin:0;font-size:1.25rem;font-weight:800;color:#1a1a1a;max-width:380px;';
      var p = document.createElement('p');
      p.style.cssText = 'margin:0;max-width:360px;color:#444;font-size:1rem;line-height:1.5;';
      p.textContent = 'Vi bekrefter den automatisk om et øyeblikk – du får også en bekreftelse på e-post.';
      pendingEl.appendChild(h);
      pendingEl.appendChild(p);
      pendingEl.appendChild(telEl('phone', 'Lurer du på noe? Ring oss: '));
      body.appendChild(pendingEl);
      // No confirmation within 4 min (organic visitor without poll keys, or a
      // slow sync): soften to "check your email" — never a hard claim.
      pendingMorphTimer = clearTimer(pendingMorphTimer);
      pendingMorphTimer = setTimeout(function () {
        if (successShown || !pendingShown) return;
        h.textContent = 'Sjekk e-posten din for bekreftelse';
        p.textContent = 'Fikk du e-post fra oss, er alt i orden. Ingen e-post? Ring oss, så hjelper vi deg på sekunder.';
      }, 4 * 60 * 1000);
    }
    function removePending() {
      if (pendingEl) { try { pendingEl.remove(); } catch (e) {} pendingEl = null; }
      pendingShown = false;
      pendingMorphTimer = clearTimer(pendingMorphTimer);
    }

    // ---- success overlay -------------------------------------------------
    function showSuccess() {
      if (successShown) return;
      successShown = true;
      removeEscape();
      removePending();
      clearInfo(); hintShown = false;
      stopTimers(); // booking confirmed — no more nudges/polls
      if (skeleton) skeleton.setAttribute('hidden', '');
      if (host) host.setAttribute('aria-hidden', 'true');
      if (success) {
        success.classList.add('is-visible');
        try { success.focus({ preventScroll: true }); } catch (e) {}
      }
    }
    function hideSuccess() {
      if (success) success.classList.remove('is-visible');
      if (host) host.removeAttribute('aria-hidden');
      successShown = false;
    }

    function resetOverlays() {
      hideSuccess();
      removeEscape();
      removePending();
      arbiterTimer = clearTimer(arbiterTimer); // a stale verdict must not cover a fresh calendar
      clearInfo();
      hintShown = false;
      if (skeleton) skeleton.removeAttribute('hidden');
      if (skeleton) skeleton.classList.remove('is-fading');
    }

    function clearTimer(t) { if (t) { clearTimeout(t); } return null; }
    function stopTimers() {
      stallTimer = clearTimer(stallTimer);
      hintTimer = clearTimer(hintTimer);
      dwellTimer = clearTimer(dwellTimer);
      arbiterTimer = clearTimer(arbiterTimer);
      pendingMorphTimer = clearTimer(pendingMorphTimer);
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    }

    // Re-arm (or stretch) the server poll so a booking completed late in the
    // session still gets its authoritative confirmation.
    function extendPoll() {
      pollStop = Math.max(pollStop, Date.now() + 5 * 60 * 1000);
      if (!pollTimer) startBookingPoll();
    }

    function fadeSkeleton() {
      if (!skeleton) return;
      skeleton.classList.add('is-fading');
      setTimeout(function () { if (skeleton) skeleton.setAttribute('hidden', ''); }, 280);
    }

    // Fresh calendar on demand ("Tilbake til kalenderen" after a booking, or a
    // reopen): never reveal the dead post-booking frame — tear it out and boot
    // a new widget under the skeleton.
    function rebuildCalendar() {
      if (host) host.innerHTML = '';
      iframeBuilt = false;
      iframeLoads = 0;
      arbiterTimer = clearTimer(arbiterTimer);
      if (skeleton) { skeleton.removeAttribute('hidden'); skeleton.classList.remove('is-fading'); }
      buildIframe();
    }

    // ---- iframe ----------------------------------------------------------
    function buildIframe() {
      if (iframeBuilt) return;
      iframeBuilt = true;
      try {
        var ifr = document.createElement('iframe');
        // (2026-06-11) The nono_* click-id param probe is GONE: Momence discards
        // unknown widget params (verified via a real test booking — nothing
        // surfaced on the member/sale), and an extra variable in the booking
        // path buys nothing. Keep the widget URL exactly as configured.
        ifr.setAttribute('src', iframeUrl);
        ifr.setAttribute('allow', 'payment; clipboard-write');
        ifr.setAttribute('title', 'Book gratis prøvetime hos Nono Moss');
        ifr.setAttribute('loading', 'eager');
        ifr.setAttribute('referrerpolicy', 'origin');
        ifr.addEventListener('load', function () {
          iframeLoads += 1;
          if (iframeLoads === 1) {
            stallTimer = clearTimer(stallTimer);
            // A slow-but-working iframe that already triggered escape: recover it.
            if (escapeShown) removeEscape();
            if (skeleton) {
              skeleton.classList.add('is-fading');
              setTimeout(function () { if (skeleton) skeleton.setAttribute('hidden', ''); }, 280);
            }
            armPostLoad();
            return;
          }
          // 2nd+ load = Momence navigated the whole frame. In the free-trial
          // appointment flow (no payment step) the ONLY full-document navigation is
          // the post-booking redirect: Momence sends the frame to nono.no/pages/booket,
          // which Shopify serves with X-Frame-Options:DENY — so the frame would
          // otherwise show the browser's "refused to connect" page (desktop) or go
          // blank (mobile). Treat this navigation as completion and cover it with our
          // branded "Booket!" confirmation. Guarded by modalEverOpened so a stray
          // prewarm reload can't fire it. (The server-poll + dwell remain as backups;
          // showSuccess is idempotent.)
          // ARBITER (2026-06-11, replaces instant success-on-2nd-load): wait 8s
          // and listen. The real post-booking redirect lands on the XFO-blocked
          // /pages/booket — a frame that CANNOT postMessage. A mid-flow bounce
          // (webview cookie hop, Momence login, etc.) reloads the live calendar,
          // which keeps posting messages. Mute frame => probably booked: show an
          // honest "we're confirming" cover (poll upgrades it to Booket!).
          // Talking frame => visitor is still mid-flow: stay out of the way.
          if (modalEverOpened) {
            load2At = Date.now();
            // Cover the frame with the neutral loading shimmer while we listen
            // — never the browser's raw "refused to connect" page. The skeleton
            // is true in both cases: either the calendar is rebooting (it will
            // be uncovered the moment it talks) or the dead post-booking page
            // is "loading" into the pending overlay.
            if (skeleton) { skeleton.removeAttribute('hidden'); skeleton.classList.remove('is-fading'); }
            if (arbiterTimer) clearTimeout(arbiterTimer);
            arbiterTimer = setTimeout(function () {
              arbiterTimer = null;
              if (successShown || escapeShown) return;
              if (momenceMsgAt > load2At) { fadeSkeleton(); return; } // calendar alive — uncover it
              showPending();
              if (!bookedBeaconSent) sendBookedBeacon(ifr);
              extendPoll();
            }, 8000);
          }
        });
        ifr.addEventListener('error', function () {
          stallTimer = clearTimer(stallTimer);
          showEscape();
        });
        host.appendChild(ifr);
      } catch (e) {
        showEscape();
      }
    }

    // After the calendar first loads:
    //  - IN-APP webviews only: a non-blocking help strip with "open in a real
    //    browser" (the lifeline where the Momence iframe can silently break).
    //    Real browsers (Chrome/Safari/etc.) work reliably, so we DON'T show it
    //    there — "open in browser" is meaningless when you're already in one.
    //  - ALL browsers: a later dwell nudge ("did you book? check your email")
    //    so a silent successful booking is never a dead end. No pointless link.
    function armPostLoad() {
      // (2026-06-16) Removed the 1.6s "Trenger du hjelp? / Åpne i nettleseren"
      // help strip. Booking was verified working end-to-end in the FB/IG webview
      // on iPhone, so nagging 1.6s after a SUCCESSFUL calendar load only cluttered
      // the cramped in-app screen — it covered the calendar's own service/time
      // controls and dangled an escape button that loops back to the landing page.
      // Genuine failures still surface the escape overlay (iframe `error` + the 8s
      // zero-load stall in openModal); the dwell nudge below still catches a silent
      // booking. We never proactively interrupt a calendar that is working.
      if (!dwellTimer) {
        // 3 min, not 75s: a 50+ booker is often still picking a slot at 75s —
        // asking "fikk du booket?" mid-selection reads as "you should be done".
        dwellTimer = setTimeout(function () { if (!successShown) showDwell(); }, 180000);
      }
    }

    // ---- server-side booking confirmation poll (primary success signal) --
    function startBookingPoll() {
      if (!attributionEndpoint || pollTimer) return;
      var fbclid = null;
      try { fbclid = new URL(window.location.href).searchParams.get('fbclid'); } catch (e) {}
      var fbp = parseCookies()._fbp || null;
      if (!fbclid && !fbp) return; // nothing to match a booking on
      var qs = [];
      if (fbclid) qs.push('fbclid=' + encodeURIComponent(fbclid));
      if (fbp) qs.push('fbp=' + encodeURIComponent(fbp));
      // Scope to THIS session: only bookings matched after the modal opened
      // may confirm — a booking from 15 min ago must not instantly "Booket!"
      // a fresh modal (book-a-second-person flow).
      qs.push('since=' + (modalOpenedAt || Date.now()));
      pollStop = Date.now() + 5 * 60 * 1000; // poll up to 5 min
      pollTimer = setInterval(function () {
        if (successShown || Date.now() > pollStop) { if (pollTimer) { clearInterval(pollTimer); pollTimer = null; } return; }
        fetch(attributionEndpoint + '/api/booking-status?' + qs.join('&'), { credentials: 'omit' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (d) { if (d && d.booked) showSuccess(); })
          .catch(function () {});
      }, 5000);
    }

    // ---- tracking --------------------------------------------------------
    function fireInitiateCheckout() {
      if (initiateFired) return;
      initiateFired = true;
      sendModalOpenBeacon();
      if (window.fbq) {
        try {
          var catEl = document.querySelector('[data-page-category]');
          window.fbq('track', 'InitiateCheckout', {
            content_category: catEl ? catEl.getAttribute('data-page-category') : 'booking',
            content_name: document.title
          });
        } catch (e) {}
      }
    }
    function sendModalOpenBeacon() {
      if (!attributionEndpoint) return;
      try {
        var cookies = parseCookies();
        var url = new URL(window.location.href);
        var catEl = document.querySelector('[data-page-category]');
        var payload = {
          event: 'lp_modal_open',
          fbclid: url.searchParams.get('fbclid'),
          fbp: cookies._fbp || null,
          fbc: cookies._fbc || null,
          eventSourceUrl: window.location.href,
          pageCategory: catEl ? catEl.getAttribute('data-page-category') : null,
          referrer: document.referrer || null
        };
        fetch(attributionEndpoint + '/api/visit-attribution', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload), keepalive: true, credentials: 'omit'
        }).catch(function () {});
      } catch (e) {}
    }
    // Booking-completion beacon (observational only — never touches modal/iframe
    // state). Called from the iframe load handler when the production-trusted
    // success signal fires (modal opened + 2nd full-document load = the
    // post-booking redirect; same gate as showSuccess). The location read is
    // OPPORTUNISTIC only: XFO:DENY on /pages/booket makes the error frame's
    // origin opaque in most browsers, so it usually throws — bookedQuery stays
    // null and the beacon still goes out. At most one per page view. Cannot throw.
    function sendBookedBeacon(ifr) {
      try {
        if (bookedBeaconSent || !attributionEndpoint) return;
        var bookedQuery = null;
        try {
          var loc = ifr.contentWindow.location;
          bookedQuery = (loc.pathname + (loc.search || '')).slice(0, 400);
        } catch (xo) {} // expected: XFO error page has an opaque origin
        bookedBeaconSent = true;
        var cookies = parseCookies();
        var url = new URL(window.location.href);
        var catEl = document.querySelector('[data-page-category]');
        var payload = {
          event: 'lp_booked',
          fbclid: url.searchParams.get('fbclid'),
          fbp: cookies._fbp || null,
          fbc: cookies._fbc || null,
          eventSourceUrl: window.location.href,
          pageCategory: catEl ? catEl.getAttribute('data-page-category') : null,
          referrer: document.referrer || null,
          bookedQuery: bookedQuery
        };
        fetch(attributionEndpoint + '/api/visit-attribution', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload), keepalive: true, credentials: 'omit'
        }).catch(function () {});
      } catch (e) {}
    }

    // ---- cookie banner de-conflict ---------------------------------------
    function hideCookieBanner() {
      cookieBanner = cookieBanner || document.getElementById('nono-cookie-banner');
      if (cookieBanner && cookieBanner.getAttribute('data-visible') === 'true') {
        cookieBanner.setAttribute('data-booking-stashed', '1');
        cookieBanner.setAttribute('data-visible', 'false');
      }
    }
    function restoreCookieBanner() {
      if (cookieBanner && cookieBanner.getAttribute('data-booking-stashed') === '1') {
        cookieBanner.removeAttribute('data-booking-stashed');
        cookieBanner.setAttribute('data-visible', 'true');
      }
    }

    // ---- focus trap (keeps keyboard/SR users on the booking surface) -----
    function focusables() {
      if (!panel) return [];
      return Array.prototype.filter.call(
        panel.querySelectorAll('a[href],button:not([disabled]),iframe,[tabindex]:not([tabindex="-1"])'),
        function (el) { return el.offsetParent !== null || el.tagName === 'IFRAME'; }
      );
    }
    function onTrapKeydown(e) {
      if (e.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); try { last.focus(); } catch (x) {} }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); try { first.focus(); } catch (x) {} }
    }

    // ---- scroll lock (iOS-robust) ----------------------------------------
    function lockScroll() {
      scrollY = window.scrollY || window.pageYOffset || 0;
      document.documentElement.style.overflow = 'hidden';
      var b = document.body.style;
      b.position = 'fixed'; b.top = (-scrollY) + 'px'; b.left = '0'; b.right = '0'; b.width = '100%';
    }
    function unlockScroll() {
      document.documentElement.style.overflow = '';
      var b = document.body.style;
      b.position = ''; b.top = ''; b.left = ''; b.right = ''; b.width = '';
      window.scrollTo(0, scrollY);
    }

    // ---- open / close ----------------------------------------------------
    function openModal() {
      if (!modal.hasAttribute('hidden')) return; // idempotent
      modalEverOpened = true;
      modalOpenedAt = Date.now(); // scopes the booking-status poll to THIS session
      resetOverlays();
      // If a prior booking navigated the iframe away from the calendar (the
      // post-booking redirect to /pages/booket), the frame is now showing that
      // dead/XFO page. Rebuild a fresh calendar so re-opening (e.g. to book a
      // second person) never lands on the post-booking page.
      if (iframeBuilt && iframeLoads > 1) {
        if (host) host.innerHTML = '';
        iframeBuilt = false;
        iframeLoads = 0;
      }
      buildIframe();
      addFooterEscape();
      modal.removeAttribute('hidden');
      lockScroll();
      hideCookieBanner();
      document.addEventListener('keydown', onTrapKeydown, true);
      // Pre-load stall: if the calendar never even loads, show the escape after 8s.
      stallTimer = clearTimer(stallTimer);
      stallTimer = setTimeout(function () { if (modalEverOpened && iframeLoads === 0) showEscape(); }, 8000);
      // If the iframe already loaded (prewarm), arm the post-load affordances now.
      if (iframeLoads >= 1) { if (skeleton) skeleton.setAttribute('hidden', ''); armPostLoad(); }
      lastFocused = document.activeElement;
      var closeBtn = modal.querySelector('[data-booking-close]');
      if (closeBtn && closeBtn.focus) { try { closeBtn.focus({ preventScroll: true }); } catch (e) { closeBtn.focus(); } }
      fireInitiateCheckout();
      startBookingPoll();
    }
    function closeModal() {
      stopTimers();
      resetOverlays();
      modal.setAttribute('hidden', '');
      unlockScroll();
      restoreCookieBanner();
      document.removeEventListener('keydown', onTrapKeydown, true);
      if (lastFocused && lastFocused.focus) { try { lastFocused.focus({ preventScroll: true }); } catch (e) {} }
    }

    // Expose opener + flush any tap captured before init finished.
    opener = openModal;
    modal.__open = openModal;
    if (pendingOpen) { pendingOpen = false; openModal(); }

    function bindTrigger(el) {
      if (!el || el.dataset.bookingTriggerBound === '1') return;
      el.dataset.bookingTriggerBound = '1';
      el.addEventListener('click', function (e) { e.preventDefault(); openModal(); });
    }
    Array.prototype.forEach.call(document.querySelectorAll('[data-booking-trigger]'), bindTrigger);

    Array.prototype.forEach.call(
      modal.querySelectorAll('[data-booking-close]'),
      function (c) { c.addEventListener('click', function (e) { e.preventDefault(); closeModal(); }); }
    );
    // "Tilbake til kalenderen" inside the success overlay (makes a false-positive
    // recoverable; a real booker can also just close).
    Array.prototype.forEach.call(
      modal.querySelectorAll('[data-booking-success-dismiss]'),
      function (c) {
        c.addEventListener('click', function (e) {
          e.preventDefault();
          hideSuccess();
          removePending();
          // After a booking the frame holds the dead post-booking page —
          // "back to the calendar" must mean a FRESH calendar, never the corpse.
          if (iframeLoads > 1) rebuildCalendar();
        });
      }
    );

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hasAttribute('hidden')) closeModal();
    });

    // ---- messages from inside the modal ----------------------------------
    window.addEventListener('message', function (e) {
      // (1) Same-origin confirmation (our /pages/booket, if it ever renders in
      //     frame or Momence redirects the top window). Strict origin check.
      if (e.origin === window.location.origin) {
        var od = e.data;
        if (od && (od.type === 'nono:booking-complete' || od === 'nono:booking-complete')) showSuccess();
        return;
      }
      // (2) Cross-origin Momence messages are NEVER treated as success (no
      //     substring guessing). We only note the frame is alive, and — when
      //     explicitly debugging — beacon a bounded sample to learn Momence's
      //     real message contract for a future exact-match upgrade.
      if (!e.origin || e.origin.indexOf('momence.com') === -1) return;
      momenceMessageSeen = true;
      momenceMsgAt = Date.now();
      // The calendar is alive — if the arbiter covered it (skeleton during the
      // listening window, or the pending overlay when a slow webview boot beat
      // the 8s window), uncover it so the visitor can finish their booking.
      // Poll-confirmed success never tears down.
      if (!successShown) {
        if (pendingShown) removePending();
        if (iframeLoads >= 2 && skeleton && !skeleton.hasAttribute('hidden')) fadeSkeleton();
      }
      if (debug && attributionEndpoint && momenceMsgCount < 5) {
        momenceMsgCount += 1;
        try {
          var sample = typeof e.data === 'string' ? e.data : JSON.stringify(e.data || '');
          fetch(attributionEndpoint + '/api/visit-attribution', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ event: 'momence_msg', referrer: e.origin, pageCategory: String(sample).slice(0, 180) }),
            keepalive: true, credentials: 'omit'
          }).catch(function () {});
        } catch (x) {}
      }
    });

    // ---- pre-warm (never in a fragile in-app webview) --------------------
    if (prewarm && !inApp) {
      var warmed = false;
      var warm = function () { if (warmed) return; warmed = true; buildIframe(); };
      ['pointerdown', 'touchstart', 'scroll', 'keydown', 'mousemove'].forEach(function (ev) {
        window.addEventListener(ev, warm, { once: true, passive: true });
      });
      // No timer fallback on mobile: the CPU goes idle while the radio is
      // still busy, so an idle/timeout prewarm loads the whole Momence app
      // (~1MB + ~1s main-thread) mid-load and starves the hero paint. First
      // touch/scroll is plenty of head start; the modal-open handler builds
      // the iframe on demand regardless.
      if (!(window.matchMedia && window.matchMedia('(max-width: 749px)').matches)) {
        if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 6000 });
        else setTimeout(warm, 4000);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
